import { useMemo, useState } from 'react';

import { configApi } from '@/api/services/configApi';
import { Spinner, TextBox } from '@/components/ui';
import type { ChinaAppSysConfig } from '@/types/entities';

import { AdminNotice } from '../AdminNotice';
import { useAdminList } from './adminList';

/** Konfigurationseinträge — Port von `IwmAdminListAllgemein.razor` (REQ-29 §4). */
const load = async (): Promise<ChinaAppSysConfig[]> =>
  // Wie heute: nur änderbare Einträge stehen in der Liste.
  (await configApi.get()).filter((entry) => entry.Editable);

export function AdminConfigList() {
  const { rows, setRows, loading, error } = useAdminList(load);
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return rows;
    return rows.filter((row) =>
      `${row.Titel ?? ''} ${row.Beschreibung ?? ''} ${row.Value ?? ''}`
        .toLowerCase()
        .includes(needle),
    );
  }, [rows, search]);

  const replace = (row: ChinaAppSysConfig) => {
    setRows((current) => current.map((entry) => (entry.Id === row.Id ? row : entry)));
  };

  /**
   * Optimistisch, mit Rückrollen. Ohne Schlüssel wird nichts gespeichert — die Regel steht
   * so im Altclient (`if (!String.IsNullOrEmpty(args.Data.Key))`); der Endpunkt ist
   * `PUT config/{key}`.
   */
  const save = async (row: ChinaAppSysConfig, value: string) => {
    if (row.Key === null || row.Key === '') return;
    const next = { ...row, Value: value };
    replace(next);
    const result = await configApi.put(row.Key, next);
    replace(result ?? row);
  };

  const content = () => {
    if (loading) {
      return (
        <div className="iwm-state-loading">
          <span className="iwm-state-spinner">
            <Spinner size={20} />
          </span>
          Wird geladen …
        </div>
      );
    }
    if (error !== null) {
      return (
        <AdminNotice tone="danger">Die Konfiguration konnte nicht geladen werden.</AdminNotice>
      );
    }
    if (rows.length === 0) {
      return <AdminNotice tone="warning">Kein änderbarer Eintrag vorhanden.</AdminNotice>;
    }

    return (
      <div className="iwm-admin-sublist-wrap">
        <table className="iwm-admin-sublist-table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Description</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.Id}>
                <td>{row.Titel}</td>
                <td>{row.Beschreibung}</td>
                <td>
                  <TextBox
                    className="iwm-admin-cell-field"
                    label={`Value: ${row.Titel ?? row.Key ?? ''}`}
                    value={row.Value}
                    onChange={(value) => {
                      void save(row, value);
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <section className="iwm-admin-sublist">
      <span className="e-input-group iwm-admin-search">
        <input
          type="search"
          className="e-input"
          placeholder="Search"
          aria-label="Search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <span className="e-input-group-icon e-search-icon e-icons fa-solid fa-magnifying-glass" />
      </span>
      {content()}
      <p className="iwm-admin-sublist-hint">
        Der Wert wird gespeichert, sobald Sie das Feld verlassen.
      </p>
    </section>
  );
}

export default AdminConfigList;
